<script setup lang="ts">
    import { CardTitle } from '@/components/ui/card';
    import { columns } from '@/tables/columns/credit-cards';
    import { computed } from 'vue';
    import { handleQueryRetry } from '@/utils/helpers';
    import { Head } from '@inertiajs/vue3';
    import { usePage } from '@inertiajs/vue3';
    import { useQuery } from '@tanstack/vue-query';
    import AppLayout from '@/layouts/AppLayout.vue';
    import axios from 'axios';
    import CreateCreditCard from '@/components/credit-cards/CreateCreditCard.vue';
    import DataTable from '@/components/datatable/DataTable.vue';
    import DataTableToolbarActions from '@/components/datatable/DataTableToolbarActions.vue';
    import PageCard from '@/components/PageCard.vue';
    import QueryKey from '@/enums/QueryKey';
    import type { SharedData, BreadcrumbItem } from '@/types';

    const page = usePage<SharedData>();
    const selectedCompanyId = computed(() => page.props.auth.user.selected_company_id);

    const breadcrumbs: BreadcrumbItem[] = [
        {
            title: 'menu.administration',
        },
        {
            title: 'menu.credit_cards',
        },
    ];

    const { isPending, data } = useQuery({
        queryKey: [QueryKey.CREDIT_CARDS, selectedCompanyId],
        queryFn: getCreditCards,
        retry: handleQueryRetry,
    });

    async function getCreditCards() {
        const { data } = await axios.get(
            route('v2.credit-cards.table', { company: selectedCompanyId.value }),
        );
        return data.data;
    }
</script>

<template>
    <Head :title="$t('credit-cards.page_header')" />

    <AppLayout
        :breadcrumbs="breadcrumbs"
        require-selected-company
    >
        <PageCard>
            <DataTable
                :table-key="QueryKey.CREDIT_CARDS"
                :columns="columns"
                :data="data"
                :loading="isPending"
                :default-sorting="[
                    {
                        id: 'name',
                        desc: false,
                    },
                ]"
            >
                <template #title>
                    <CardTitle>
                        {{ $t('credit-cards.page_header') }}
                    </CardTitle>
                </template>
                <template #actions>
                    <DataTableToolbarActions name="credit-cards">
                        <CreateCreditCard />
                    </DataTableToolbarActions>
                </template>
            </DataTable>
        </PageCard>
    </AppLayout>
</template>
